import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Partner = () => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="max-w-md text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-[#115E59]" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Welcome Aboard
          </h1>
          <p className="text-slate-500 mb-8">
            Thank you, {form.full_name}. We're excited about the potential partnership. Our team will be in touch shortly.
          </p>
          <Link to="/" className="text-sm font-semibold text-[#115E59] hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <div className="relative bg-slate-900 text-white py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d9f0d756-7ea7-4320-ba4f-34d7f82a2718/images/8ecd4e1023335e6a6e72291ecbce4c93ba08cb3dd6741296364875c979d1e9fb.png)` }}
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-teal-300 mb-4 block">Referral Partners</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Partner With Quidity
          </h1>
          <p className="text-slate-400 max-w-lg mx-auto">
            Earn competitive commissions by referring qualified businesses to our financing programs.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { title: 'Competitive Commissions', desc: 'Earn generous referral fees on every funded deal.' },
            { title: 'Dedicated Support', desc: 'Your own partner manager to help close deals.' },
            { title: 'Real Time Tracking', desc: 'Monitor your referrals and commissions online.' },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="text-sm font-bold text-slate-900 mb-2">{b.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-application-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Full Name *</label>
              <input data-testid="partner-full-name" name="full_name" required value={form.full_name} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Email *</label>
              <input data-testid="partner-email" name="email" type="email" required value={form.email} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="jane@firm.com" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Phone *</label>
              <input data-testid="partner-phone" name="phone" type="tel" required value={form.phone} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="(555) 987-6543" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company</label>
              <input data-testid="partner-company" name="company" value={form.company} onChange={handleChange}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all" placeholder="Your Company" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Message</label>
            <textarea data-testid="partner-message" name="message" rows={4} value={form.message} onChange={handleChange}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-sm text-slate-800 focus:ring-2 focus:ring-[#115E59] focus:border-transparent transition-all resize-none" placeholder="Tell us about your business and how you'd like to partner with us..." />
          </div>

          {error && <div data-testid="partner-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</div>}

          <button
            data-testid="partner-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white font-semibold text-sm py-4 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting...' : 'Apply to Partner'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Partner;
